import React, { useState } from 'react';
import { useOne, useList } from "@refinedev/core";
import { useParams, useNavigate } from 'react-router-dom';
import EditProject from './edit';

interface IMDFFile {
  id: string;
  originalName: string;
  storagePath: string;
  description?: string;
  version?: string;
  category?: string;
  fileSize: number;
  status: 'pending' | 'validating' | 'valid' | 'invalid';
  createdAt: string;
  updatedAt: string;
}

interface Project {
  id: string;
  name: string;
  description: string;
  creator: string;
  imdfSet: string[];
  createdAt: string;
  updatedAt: string;
  lastModifiedBy: string;
  apiKey: string;
}

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // 取得專案詳細資訊
  const { data: projectData, isLoading: projectLoading, error: projectError } = useOne<Project>({
    resource: "projects",
    id: id || "",
  });

  // 取得所有 IMDF 檔案，用於顯示專案中的檔案詳情
  const { data: allImdfData, isLoading: imdfLoading } = useList<IMDFFile>({
    resource: "imdf",
    config: {
      pagination: {
        mode: "off"
      }
    }
  });

  // 返回專案列表
  const handleBackClick = () => {
    navigate('/projects');
  };

  // 打開編輯視窗
  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  // 格式化日期
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 格式化檔案大小
  const formatFileSize = (bytes: number): string => {
    const sizes = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let size = bytes;
    while (size >= 1024 && i < sizes.length - 1) {
      size /= 1024;
      i++;
    }
    return `${size.toFixed(1)} ${sizes[i]}`;
  };

  // 取得專案中的 IMDF 檔案
  const projectImdfFiles = allImdfData?.data
    .filter(file => projectData?.data.imdfSet.includes(file.id)) || [];

  // 獲取檔案狀態樣式
  const getStatusClasses = (status: IMDFFile['status']): string => {
    const statusClasses: Record<IMDFFile['status'], string> = {
      valid: "bg-green-100 text-green-800",
      invalid: "bg-red-100 text-red-800",
      pending: "bg-yellow-100 text-yellow-800",
      validating: "bg-blue-100 text-blue-800",
    };
    return `px-2 py-1 rounded-full text-xs font-medium ${statusClasses[status]}`;
  };

  // 處理下載 IMDF 檔案
  const handleDownloadClick = (fileId: string): void => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    window.location.href = `${API_URL}/api/imdf/files/${fileId}/download`;
  };

  if (projectLoading || imdfLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (projectError || !projectData) {
    return (
      <div className="p-8 text-center text-gray-500">
        無法載入專案資料，請稍後再試
      </div>
    );
  }

  const project = projectData.data;

  return (
    <div className="space-y-6">
      {/* 頂部導航和按鈕 */}
      <div className="flex justify-between items-center">
        <button
          onClick={handleBackClick}
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L4.414 9H17a1 1 0 110 2H4.414l5.293 5.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          返回專案列表
        </button>
        <button
          onClick={handleEditClick}
          className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
        >
          編輯專案
        </button>
      </div>

      {/* 專案詳情卡片 */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">{project.name}</h2>
        </div>
        <div className="px-6 py-4 space-y-4">
          {/* 專案基本資訊 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">描述</h3>
              <p className="mt-1 text-sm text-gray-900">{project.description || '無描述'}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">建立者</h3>
              <p className="mt-1 text-sm text-gray-900">{project.creator}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">建立時間</h3>
              <p className="mt-1 text-sm text-gray-900">{formatDate(project.createdAt)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">最後更新</h3>
              <p className="mt-1 text-sm text-gray-900">
                {formatDate(project.updatedAt)}
                <span className="text-xs text-gray-500 ml-1">
                  (由 {project.lastModifiedBy} 更新)
                </span>
              </p>
            </div>
          </div>

          {/* API Key */}
          <div>
            <h3 className="text-sm font-medium text-gray-500">API Key</h3>
            <div className="mt-1 flex items-center">
              <code className="px-2 py-1 text-sm font-mono bg-gray-100 rounded text-gray-800 flex-grow">
                {project.apiKey}
              </code>
              <button 
                className="ml-2 p-1 text-blue-600 hover:text-blue-800 border border-blue-600 hover:border-blue-800 rounded-md transition-colors"
                onClick={() => {
                  navigator.clipboard.writeText(project.apiKey);
                  alert('API Key 已複製到剪貼簿');
                }}
                title="複製 API Key"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* IMDF 檔案列表 */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">IMDF 檔案列表</h2>
        </div>
        <div className="overflow-x-auto">
          {projectImdfFiles.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">此專案尚未選擇 IMDF 檔案</p>
              <p className="text-sm text-gray-400 mt-2">點擊「編輯專案」來新增 IMDF 檔案</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                    檔案名稱
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                    版本
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                    分類
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                    狀態
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                    大小
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {projectImdfFiles.map((file) => (
                  <tr key={file.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {file.originalName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {file.version || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {file.category || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={getStatusClasses(file.status)}>
                        {file.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatFileSize(file.fileSize)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleDownloadClick(file.id)}
                        className="px-3 py-1 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-600 hover:border-gray-800 rounded-md transition-colors"
                      >
                        下載
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 編輯專案對話框 */}
      <EditProject
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        projectId={id || ""}
      />
    </div>
  );
};

export default ProjectDetailPage;